<template>
    <div class="login-page">
      <div class="login-container">
        <div class="login-branding">
          <img :src="systemBranding.systemLogoUrl" :alt="systemBranding.systemName" class="login-system-logo" />
          <div class="login-commerce-mark">
            <img
              v-if="commerceLogoAvailable"
              :src="commercialBranding.commerceLogoUrl"
              :alt="commercialBranding.commerceName || 'Comercio'"
              class="login-commerce-logo"
              @error="commerceLogoAvailable = false"
            />
            <span v-else class="login-commerce-name">
              {{ commercialBranding.commerceName || 'Comercio' }}
            </span>
          </div>
        </div>
        <h2 class="login-title">Acceso al POS</h2>
        <form @submit.prevent="login">
          <div class="form-group">
            <label for="username">Nombre de Usuario</label>
            <div class="input-container">
              <i class="fas fa-user"></i>
              <input
                type="text"
                id="username"
                v-model="username"
                placeholder="Ingresa tu nombre de usuario"
                required
              />
            </div>
            <div v-if="usernameError" class="error-message">{{ usernameError }}</div>
          </div>
          <div class="form-group">
            <label for="password">Contraseña</label>
            <div class="input-container">
              <i class="fas fa-lock input-leading-icon"></i>
              <input
                :type="showPassword ? 'text' : 'password'"
                id="password"
                v-model="password"
                placeholder="Ingresa tu contraseña"
                required
              />
              <button
                type="button"
                class="password-visibility-btn"
                :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                @click="togglePasswordVisibility"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <div v-if="passwordError" class="error-message">{{ passwordError }}</div>
          </div>
          <button type="submit" class="login-btn">Iniciar Sesión</button>
        </form>
        <!-- Firma comercial del sistema: el usuario entra al POS de su
             comercio, pero el software que lo mueve es VENTAX Gestión. El
             nombre interno del stack no se muestra acá — ver
             `docs/CONVENCION_MARCA_VENTAX.md`. -->
        <p class="login-system-credit">
          {{ systemBranding.systemProductName }} · {{ systemBranding.systemUrl }}
        </p>
      </div>
    </div>
</template>
  
<script>
import apiService from '@/services/apiService';
import { clearAuthSession, setAuthSession, hasPermission } from '@/services/authSessionService';
import { homeDestino } from '@/router/homeDestino.mjs';
import brandingService from '@/services/brandingService';

export default {
  name: "LoginView",
  data() {
    return {
      systemBranding: brandingService.getSystemBranding(),
      commercialBranding: brandingService.getDefaultCommercialBranding(),
      commerceLogoAvailable: false,
      username: "",
      password: "",
      showPassword: false,
      usernameError: "",
      passwordError: ""
    };
  },
  mounted() {
    clearAuthSession();
    if (this.$route.query.usuario) {
      this.username = this.$route.query.usuario;
    }
    this.loadPublicBranding();
  },
  methods: {
    async loadPublicBranding() {
      try {
        this.commercialBranding = await brandingService.getPublicBranding();
      } catch (_) {
        this.commercialBranding = brandingService.getDefaultCommercialBranding();
      }
      this.commerceLogoAvailable = Boolean(this.commercialBranding.commerceLogoUrl);
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    login() {
      this.usernameError = "";
      this.passwordError = "";

      let hasError = false;

      if (!this.username) {
        this.usernameError = "Por favor, ingresa un nombre de usuario.";
        hasError = true;
      }
      if (!this.password) {
        this.passwordError = "Por favor, ingresa una contraseña.";
        hasError = true;
      }

      const usernameRegex = /^\S{3,}$/;
      if (!hasError && !usernameRegex.test(this.username)) {
        this.usernameError = "El nombre de usuario debe tener al menos 3 caracteres y no debe contener espacios.";
        hasError = true;
      }
      if (!hasError && this.password.length < 5) {
        this.passwordError = "La contraseña debe tener al menos 5 caracteres.";
        hasError = true;
      }

      if (hasError) return;

      apiService.post('/api/auth/login', {
        usuario: this.username,
        password: this.password
      })
        .then(response => {
          const data = response.data;
          if (data.token) {
            setAuthSession(data);
            // Home por rol (RF-2 de SPEC_DASHBOARD_GERENCIAL_v0.1.md): por
            // permiso, no por nombre de rol — los datos reales tienen 10
            // roles con nombres inconsistentes
            // (administrador/Administrador, etc.), matchear por string
            // sería frágil. setAuthSession ya escribió localStorage de
            // forma síncrona, así que hasPermission ya lee el valor fresco.
            //
            // El criterio vive en `router/homeDestino.mjs` y lo comparten el
            // login, el logo y el guard. Estaba escrito acá y en `AppNavbar`
            // por separado, con el propio navbar advirtiendo que si divergían
            // «el logo lleva a un lado y el login a otro» (A-8).
            this.$router.push(homeDestino(hasPermission));
          } else {
            this.passwordError = "Usuario o contraseña incorrectos.";
          }
        })
        .catch(error => {
          console.error("Error:", error);
          const status = error.response?.status;
          const backendMessage = error.response?.data?.message || error.userMessage;

          if (status === 403 && backendMessage?.includes('activación inicial')) {
            this.$router.push({
              name: 'FirstAccess',
              query: { usuario: this.username }
            });
            return;
          }

          if (status === 423) {
            this.passwordError = backendMessage || "La cuenta se encuentra bloqueada.";
            return;
          }

          if (status === 403) {
            this.passwordError = backendMessage || "La cuenta no puede iniciar sesión.";
            return;
          }

          if (status === 401) {
            this.passwordError = backendMessage || "Usuario o contraseña incorrectos.";
            return;
          }

          this.passwordError = "Ocurrió un error al intentar iniciar sesión.";
        });
    }
  }
};
</script>
  
<style scoped>
/* Centrado de la página de inicio de sesión */
.login-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: white;
  margin: 0;
  padding: 0;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Contenedor del formulario */
.login-container {
  background-color: white;
  padding: 2.5rem;
  border-radius: 10px;
  box-shadow: 0 18px 45px rgba(8, 32, 45, 0.14);
  width: 100%;
  max-width: 440px;
  text-align: center;
  animation: fadeIn 0.8s ease-in-out;
}

.login-branding {
  display: grid;
  gap: 1.1rem;
  justify-items: center;
  margin-bottom: 1.5rem;
}

.login-system-logo,
.login-commerce-logo {
  max-width: 230px;
  width: min(70vw, 230px);
  max-height: 76px;
  object-fit: contain;
}

.login-commerce-mark {
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-commerce-name {
  display: inline-flex;
  align-items: center;
  min-height: 58px;
  color: #111827;
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1.05;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}

.login-system-credit {
  margin: 1.75rem 0 0 0;
  color: #7b8b93;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

/* Animación del formulario */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Título del formulario */
.login-title {
  font-size: 1.45rem;
  margin-bottom: 1.5rem;
  color: #132f3a;
}

/* Etiquetas */
label {
  display: block;
  font-size: 1rem;
  font-weight: bold;
  color: black;
}

/* Contenedor para los iconos */
.input-container {
  position: relative;
  width: 100%;
  display: block;
}

.input-leading-icon {
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: black;
  pointer-events: none;
}

input {
  width: 100%;
  padding: 0.9rem 2.9rem 0.9rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.password-visibility-btn {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #6c757d;
  padding: 0;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.password-visibility-btn:hover {
  color: #198754;
}

.password-visibility-btn i {
  position: static;
  transform: none;
}

input:focus {
  border-color: #07a7e1;
  box-shadow: 0 0 0 0.2rem rgba(7, 167, 225, 0.16);
  outline: none;
}

/* Botón de inicio de sesión */
.login-btn {
  width: 100%;
  padding: 0.9rem;
  background-color: #132f3a;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease;
  margin-top: 1.5rem;
}

/* Efecto hover */
.login-btn:hover {
  background-color: #07a7e1;
  transform: scale(1.05);
}

/* Mensajes de error */
.error-message {
  color: red;
  font-size: 0.85rem;
  margin-top: 0.5rem;
}

</style>
